import type { Filters } from '@/helpers/functions';
import { CacheManager } from '@/lib/cache';
import { getDecisionTypeLabelStatic } from '@/lib/utils/decisionTypeLabels';
import researchServices from '@/services/research-services';
import type { DataSourceInput } from '@/types/dataSources';
import type {
  ComparisonResult,
  DocumentAnalysisData,
  LegalIssue,
} from '@/types/document-analysis-types';
import type { NativeMessageDTO } from '@/types/history-types';
import type {
  AuthorityVerifierResult,
  ChatHistoryItem,
  ContextChipSkippedResult,
  ContextScopeChangedResult,
  ExecutedStage,
  ResponseSourceType,
  SemanticVerifierResult,
  ShimmerStageKey,
  TaskStatus,
  TemporalVerifierResult,
} from '@/types/researchTypes';
import { normalizeDataSourcesFromApi } from '@/utils/fileIdUtils';
import {
  type EntityState,
  type PayloadAction,
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';

// Runtime type guard for ResponseSourceType to prevent unsafe casts
// The database column is VARCHAR(50) without constraints, so we validate at runtime
const VALID_RESPONSE_SOURCE_TYPES: readonly ResponseSourceType[] = [
  'document_search',
  'web_search',
  'general_knowledge',
  'search_results',
  'search_documents',
  'hybrid', // deprecated: kept for DB backward compat
  'document_generation',
  'document_revision',
] as const;

function isValidResponseSourceType(
  value: unknown
): value is ResponseSourceType {
  return (
    typeof value === 'string' &&
    VALID_RESPONSE_SOURCE_TYPES.includes(value as ResponseSourceType)
  );
}

/**
 * Safely parse executed_stages which may come from API as:
 * - An array (already parsed)
 * - A JSON string (needs parsing)
 * - null/undefined
 * Returns an array or empty array if parsing fails.
 */
export function safeParseExecutedStages(
  stages: ExecutedStage[] | string | null | undefined
): ExecutedStage[] {
  if (!stages) return [];
  if (Array.isArray(stages)) return stages;
  if (typeof stages === 'string') {
    try {
      const parsed = JSON.parse(stages);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

// Create entity adapter for chat history with normalized state
// Entity adapter uses 'id' field by default, so no selectId needed
//
// Sorting: Messages arrive pre-sorted by db_id ASC from the v2 API.
// The comparator handles parent-child ordering (fallback after parent)
// and optimistic messages that don't yet have a db_id.
/**
 * Chat message sort comparator.
 *
 * Primary key: db_id (database creation order).
 * Fallback: created_at timestamp (for optimistic messages before server assigns db_id).
 *
 * Parent-child ordering (Message 2 after Message 1) is naturally correct
 * because the parent message always has a lower db_id than the child.
 */
export const chatHistorySortComparer = (
  a: ChatHistoryItem,
  b: ChatHistoryItem
): number => {
  if (a.db_id !== undefined && b.db_id !== undefined) {
    return a.db_id - b.db_id;
  }
  // Optimistic messages (no db_id yet) — fall back to timestamp
  const timeDiff =
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  if (timeDiff !== 0) return timeDiff;
  // Tiebreaker: when timestamps are identical (same millisecond), user messages
  // come before assistant messages — the user query is always submitted first.
  if (a.role === 'user' && b.role !== 'user') return -1;
  if (a.role !== 'user' && b.role === 'user') return 1;
  return 0;
};

const chatHistoryAdapter = createEntityAdapter<ChatHistoryItem>({
  sortComparer: chatHistorySortComparer,
});

// ========================================
// Chat History Caching Infrastructure
// ========================================
/**
 * In-memory cache for chat history to prevent redundant API calls
 * Key: sessionId
 * Value: { chatHistoryItems }
 *
 * Cache is automatically cleared on page refresh/navigation
 * TTL: 0 (no expiration - invalidated manually on mutations)
 */
interface ChatHistoryCacheData {
  chatHistoryItems: ChatHistoryItem[];
}

const chatHistoryCache = new CacheManager<ChatHistoryCacheData>(
  'ChatHistory',
  0 // No TTL - manually invalidated on mutations
);

// ========================================
// Chat ID Persistence (localStorage)
// ========================================
/**
 * Persist currentChatId to localStorage with session-specific key.
 * This allows retry context to survive page refreshes and new tabs.
 *
 * Key format: lexomat_retry_chatId_{sessionId}
 * Example: lexomat_retry_chatId_abc123 = "12345"
 *
 * Why localStorage instead of sessionStorage:
 * - Survives page refresh (critical for retry after refresh)
 * - Works across multiple tabs (user can retry in new tab)
 * - Session-specific keys prevent cross-session contamination
 */
const CHAT_ID_STORAGE_KEY_PREFIX = 'lexomat_retry_chatId_';

const persistChatId = (sessionId: string, chatId: number | null): void => {
  if (typeof window === 'undefined') return; // SSR safety

  const key = `${CHAT_ID_STORAGE_KEY_PREFIX}${sessionId}`;

  if (chatId === null) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, String(chatId));
  }
};

/**
 * Invalidate cache for a specific session
 * Call this when session data is mutated (new message, edit, delete)
 * Exported for use by visibility recovery hook (useSessionInitialization)
 */
export function invalidateChatHistoryCache(sessionId: string): void {
  chatHistoryCache.invalidate(sessionId);
}

// ========================================
// Shared: Transform v2 NativeMessageDTO → ChatHistoryItem
// ========================================

/**
 * Map a single v2 NativeMessageDTO to a ChatHistoryItem.
 * The v2 API returns one row per message — no splitting/grouping needed.
 */
export function nativeMessageToChatHistoryItem(
  msg: NativeMessageDTO
): ChatHistoryItem {
  const role = msg.role === 'user' ? 'user' : 'assistant';
  const dbId = msg.legacy_chat_id ?? msg.id;

  // Safely parse executed_stages (may come as JSON string from API)
  const parsedStages = safeParseExecutedStages(
    msg.executed_stages as ExecutedStage[] | string | null | undefined
  );

  // Extract thoughts from executed_stages (embedded in stage metadata)
  const embeddedThoughts =
    role === 'assistant'
      ? parsedStages.find(
          (stage) =>
            (stage.stageKey === 'refiningLegislationStatus' ||
              stage.stageKey === 'webSearchFallback') &&
            stage.thoughts
        )?.thoughts
      : undefined;

  // Map v2 status to legacy task_status for UI rendering
  const taskStatus =
    msg.status === 'streaming'
      ? ('running' as TaskStatus)
      : (msg.status as TaskStatus);

  return {
    id: `${role}-${dbId}`,
    db_id: dbId,
    created_at: new Date(msg.created_at).toISOString(),
    content: msg.content ?? '',
    role,
    // Group chat (#2607): carry the author on user turns so history-loaded
    // messages from other members render left with their avatar/name (the live
    // + catch-up mappers already set this). Own turns self-suppress the label.
    sender_id: role === 'user' ? msg.user_id : undefined,
    thinking: embeddedThoughts || undefined,
    data_sources: normalizeDataSourcesFromApi(msg.data_sources) || undefined,
    task_status: role === 'assistant' ? taskStatus : undefined,
    has_document_backing: msg.has_document_backing ?? undefined,
    response_source_type: isValidResponseSourceType(msg.response_source_type)
      ? msg.response_source_type
      : undefined,
    // PR #4655: explicit per-source flags. `null` from DB means the row pre-dates
    // this PR — `undefined` falls through to MessageActions' fallback derivation.
    used_web_search: msg.used_web_search ?? undefined,
    used_document_analysis: msg.used_document_analysis ?? undefined,
    // Issue #5862: web source authority gate. `null` from DB = no web sources used
    // (or row pre-dates the feature) — standard disclaimer applies.
    web_sources_all_official: msg.web_sources_all_official ?? undefined,
    // Issue #5948/#5951: source-class v2 — legal kind of official web sources and
    // of the DB backing. `null` from DB = no signal (or row pre-dates the feature).
    web_official_class: msg.web_official_class ?? undefined,
    db_backing_class: msg.db_backing_class ?? undefined,
    // Issue #5950: legal-state date the answer reflects. `null` from DB = no
    // DB-backed temporal context (or row pre-dates the feature).
    legal_state_date: msg.legal_state_date ?? undefined,
    parent_message_id: msg.parent_message_id ?? undefined,
    executedStages: parsedStages.length > 0 ? parsedStages : undefined,
    search_results_data:
      (msg.search_results_data as ChatHistoryItem['search_results_data']) ??
      undefined,
    search_metadata:
      (msg.search_metadata as ChatHistoryItem['search_metadata']) ?? undefined,
    document_analysis_data:
      (msg.document_analysis_data as ChatHistoryItem['document_analysis_data']) ??
      undefined,
    citation_metadata:
      (msg.citation_metadata as ChatHistoryItem['citation_metadata']) ??
      undefined,
    // Issue #6330: document generation source list.
    sources: (msg.sources as ChatHistoryItem['sources']) ?? undefined,
    // Issue #7155: artifact reference for the per-message document chip.
    document_version_number: msg.document_version_number ?? undefined,
    // Issue #7177 Level 2: attached editor selection (user revision messages).
    selected_text: msg.selected_text ?? undefined,
  };
}

// ========================================
// Async Thunk: Fetch Chat History (v2 native format)
// ========================================
/**
 * Fetches chat history from the v2 endpoint (native message format).
 * Each message is an individual row — no legacy user/model split needed.
 */
export const fetchChatHistory = createAsyncThunk(
  'researchSession/fetchChatHistory',
  async ({ sessionId }: { sessionId: string }) => {
    // Check cache before making API call
    const cached = chatHistoryCache.get(sessionId);
    if (cached) {
      return cached;
    }

    // v2 endpoint returns NativeMessageDTO[] — one per message row
    const response = await researchServices.getSessionHistory(sessionId);
    const rawMessages = (response.data || []) as NativeMessageDTO[];

    // Transform: direct 1:1 mapping (no grouping/splitting)
    const chatHistoryItems = rawMessages
      .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
      .map(nativeMessageToChatHistoryItem);

    const result = { chatHistoryItems };

    chatHistoryCache.set(sessionId, result);

    return result;
  }
);

/**
 * Fetches paginated chat history using cursor-based pagination (v2 native format).
 * Used for initial load (no cursor) and "load more" (with cursor).
 */
const fetchChatHistoryPaginated = createAsyncThunk(
  'researchSession/fetchChatHistoryPaginated',
  async ({
    sessionId,
    limit = 50,
    cursor,
  }: {
    sessionId: string;
    limit?: number;
    cursor?: number;
  }) => {
    const response = await researchServices.getSessionHistory(sessionId, {
      limit,
      ...(cursor !== undefined ? { cursor } : {}),
    });

    const rawMessages = (response.data || []) as NativeMessageDTO[];
    const meta = response.meta as {
      has_more: boolean;
      next_cursor: number | null;
    };

    const chatHistoryItems = rawMessages
      .filter((msg) => msg.role === 'user' || msg.role === 'assistant')
      .map(nativeMessageToChatHistoryItem);

    return {
      chatHistoryItems,
      hasMore: meta.has_more,
      nextCursor: meta.next_cursor,
      isLoadMore: cursor !== undefined,
    };
  }
);

/**
 * Issue #886: Translate English thoughts to Slovak after streaming completes
 *
 * This thunk is called after streaming ends when thoughts are available.
 * It calls the translation API and updates the message with the Slovak translation.
 * Non-blocking - called in background after stream completes.
 */
const translateThoughtsForMessage = createAsyncThunk(
  'researchSession/translateThoughtsForMessage',
  async ({
    messageId,
    englishThoughts,
    sessionId,
    showShimmer = true,
  }: {
    messageId: string;
    englishThoughts: string;
    sessionId?: string;
    /** Issue #886: When false, translation happens silently without shimmer.
     * Set to false for page reload translations where thoughts are fetched from DB. */
    showShimmer?: boolean;
  }) => {
    // Lazy import to avoid circular dependencies
    const { translateThoughts } = await import(
      '@/services/translation-services'
    );

    try {
      const response = await translateThoughts({
        thoughts: englishThoughts,
        session_id: sessionId,
      });

      if (response?.data?.translated_thoughts) {
        return {
          messageId,
          translatedThoughts: response.data.translated_thoughts,
          showShimmer,
        };
      }
    } catch (error) {
      console.error('[translateThoughtsForMessage] Translation failed:', error);
    }

    // Return original on error
    return { messageId, translatedThoughts: englishThoughts, showShimmer };
  }
);

// Define a type for the notification
interface Notification {
  message: string;
  type: 'error' | 'warning';
  showRetry?: boolean | undefined;
  errorCode?: number | undefined;
  sessionId: string; // Track which session this notification belongs to
}

interface RefinementThoughts {
  thoughts: string;
}

// Define a type for the slice state
interface ResearchSessionState {
  sessionId: string | null;
  chatHistory: EntityState<ChatHistoryItem, string>; // Changed to EntityState
  /**
   * Issue #7155: live document lane. document_output_streaming SSE chunks
   * accumulate here so the rich-text editor can render the document typing
   * itself in real time; cleared on stream start and on the authoritative
   * document_generation_complete reconciliation.
   */
  streamingDocument: string;
  currentQuery: string | null;
  currentChatId: number | null; // Current chat database ID for consistent message IDs

  // ✅ NEW: Progressive loading states (Phase 4)
  isInitializing: boolean; // Track initial session/history load
  isChatHistoryLoading: boolean; // Track progressive history batch loading
  historyBatchSize: number; // Messages per batch (default: 5)

  // ✅ NEW: Cursor-based pagination states (Faster Historical Chat Loading)
  // These enable "Load more" button for sessions with many messages
  hasMoreMessages: boolean; // True if there are older messages to load
  nextCursor: number | null; // ID to fetch messages BEFORE (null = no more messages)
  isLoadingMore: boolean; // True while loading older messages

  streaming: boolean;
  queryAnalyzing: boolean;
  enhanceQueryAnalyzing: string;
  searchRelatedResults: boolean;
  filteringLegislationStatus: string | null; // Consider a more specific type/enum later
  filterOutputStreaming: string;
  refiningLegislationStatus: string | null; // Consider a more specific type/enum later
  refinementOutputStreaming: string;
  refinementThoughts: RefinementThoughts;
  thoughtsStreaming: boolean; // New state to track thoughts streaming status
  analyseLegislation: string[];
  notification: Notification | null; // Changed from error: null
  isCancelled: boolean; // True when user explicitly cancelled — prevents post-stream fetchChatHistory

  // Issue #852: Track current response source type during streaming
  // 'document_search' | 'web_search' | 'general_knowledge' | null
  // Note: 'hybrid' (deprecated) may appear in old DB records — frontend maps it to 'document_search'
  currentResponseSourceType: string | null;

  // Issue #852: Track when waiting for fallback message (web search after document search)
  isWaitingForFallback: boolean;

  // Issue #852: Unified Streaming Model - ID of the message currently being streamed
  // When not null, a message in chatHistory with this ID has isStreaming=true
  streamingMessageId: string | null;

  // Timeline stages that have executed during the current stream
  // Accumulated during streaming, then copied to message on completion
  executedStagesQueue: ExecutedStage[];

  // Issue #886: Track messages currently being translated
  // Used to show shimmer loading state in ThoughtsSection
  translatingMessageIds: string[];

  // Session-scoped filter isolation: filters are "frozen" when a search starts
  // This prevents cross-session contamination when multiple sessions are open
  sessionFilters: SessionFilters | null;

  // Issue #886: Session-scoped smart filter loading state
  // Each session tracks its own loading state independently to prevent
  // the spinning wheel from appearing in other sessions
  autoFilterLoading: boolean;

  // Document analysis: shimmer during PDF extraction phase (before streaming message exists)
  documentExtracting: boolean;
  // Sub-text shown below "Extrahujem dokument..." (from document_extraction_progress payload)
  extractionProgressMessage: string | null;

  // Document analysis: shimmer during issue identification phase
  // Issues accumulate here and are shown live in the shimmer area, then moved to the
  // streaming message all at once when issue_identification_end fires.
  identifyingIssues: boolean;
  discoveredIssues: LegalIssue[];
  // Document analysis: shimmer during comparative analysis phase
  comparingLegislation: boolean;
  // How many comparison_result events have arrived (for progress sub-text)
  comparisonCount: number;
  // Live comparison results shown in shimmer (cleared on comparative_analysis_end)
  discoveredComparisons: Array<{
    issue_id: string;
    compliance_status: string;
    document_clause: string;
  }>;
  // Full ComparisonResult objects accumulated during comparative_analysis phase,
  // bulk-assigned to the streaming message at comparative_analysis_end.
  pendingComparisonResults: ComparisonResult[];

  // Research extraction: per-batch progress sub-text (e.g. "Čítam zákon č. 311/2001 Z. z.")
  // Set from extract_batch_progress SSE event, cleared on extract_legislation_end
  extractionBatchSubText: string | null;

  // ReAct agent: current tool being executed (null = not in tool execution)
  // '_thinking' = agent is reasoning, 'legal_search' etc. = specific tool running
  reactAgentToolName: string | null;
  // ReAct agent: contextual sub-text for shimmer (tool query, doc count, or reflection diagnosis)
  reactAgentSubText: string | null;
  // ReAct agent: iteration transition state — shows WHY the agent is retrying (#2327)
  iterationTransition: {
    quality: string;
    diagnosis: string | null;
    iteration: number;
  } | null;

  // URI short-code → full-URI mapping from backend uri_map SSE event.
  // Used to decode short codes in streaming content for progressive link rendering.
  uriMap: Record<string, string>;
  hasUriMap: boolean;

  // Issue #3817: per-URL rich source map for web grounding citations
  // (URL → { title, domain, excerpts[] }) emitted via web_sources_map SSE.
  // ExternalLinkPreview reads this so the hover-card chip can show the
  // page title + grounded excerpt for `[anchor](url)` web citations.
  webSourcesMap: Record<string, WebSourceInfo>;

  // Issue #5069: per-session temporal verifier results.
  // Populated by the `temporal_verifier_flag` SSE event (fires at most once per
  // answer, only when flags exist). Cleared at stream start so a clean later answer
  // in the same session does not show a stale panel.
  temporalVerifierBySession: Record<string, TemporalVerifierResult>;

  // Issue #5069: per-session semantic verifier results (faithfulness + scope).
  // Populated by the `semantic_verifier_flag` SSE event (fires at most once per
  // answer, only when flags exist). Cleared at stream start, same as the temporal
  // verifier above, so a clean later answer does not show a stale panel.
  semanticVerifierBySession: Record<string, SemanticVerifierResult>;

  // Issue #5161 Phase 5 (#5773): per-session authority verifier results.
  // Populated by the `authority_verifier_flag` SSE event (fires at most once per
  // answer, only when the gap is detected). Cleared at stream start, same as the
  // temporal/semantic verifiers, so a clean later answer does not show a stale panel.
  authorityVerifierBySession: Record<string, AuthorityVerifierResult>;

  // Issue #8095: per-session skipped-context-chip results.
  // Populated by the `context_chip_skipped` SSE event (fires at most once per
  // request, only when the worker's merged chip scope carries warnings — i.e. a
  // selected chip silently fell out of scope). Cleared at stream start so a
  // clean later request in the same session does not show a stale notice.
  contextChipSkippedBySession: Record<string, ContextChipSkippedResult>;

  // Per-session context-scope-change result. Populated by the
  // `context_scope_changed` SSE event (fires at most once per request, only
  // when this turn's chip scope differs from the previous turn's). Cleared at
  // stream start so a later unchanged turn in the same session does not show
  // a stale notice.
  contextScopeChangedBySession: Record<string, ContextScopeChangedResult>;
}

export interface WebSourceInfo {
  title: string;
  domain: string;
  excerpts: string[];
  // #5647 / #3983 Track B: backend tags EU-law + apex-court + SK state-portal
  // sources as authoritative (snake_case key, as emitted by the backend SSE
  // event — the payload is JSON.parse'd straight into this shape). Optional so
  // older cached payloads without the flag degrade gracefully to "not flagged".
  is_authoritative?: boolean | undefined;
  // #5987 / #5949: when an authoritative PRIMARY-SOURCE web hit (slov-lex /
  // EUR-Lex) cites an act we already hold internally, the backend resolves it to
  // the internal Lexomat document and annotates the source with the matched
  // document_content_id + a provenance flag (top-level snake_case keys, emitted
  // by the backend SSE event and JSON.parse'd straight into this shape). The
  // frontend then presents/links the citation as the internal section-anchored
  // Lexomat document instead of the raw external URL. Optional so unresolved
  // sources / older cached payloads degrade gracefully to the external link.
  internal_doc_id?: number | null | undefined;
  resolved_to_internal?: boolean | undefined;
}

/**
 * Session-scoped filter state to prevent cross-session contamination.
 * When a search/research starts, the current filters are captured here.
 * Smart filter suggestions update this session-specific state, not global filters.
 */
interface SessionFilters {
  // Filters captured at research start (from getEffectiveFilters())
  original: Filters;
  // Filters after AI smart filter narrowing (if applicable)
  afterSmartFilter?: Filters;
  // Timestamp when filters were frozen
  frozenAt: number;
  // Smart filter metadata for display in ThoughtsSection
  smartFilterApplied?: {
    searchLegislation: boolean;
    searchDecisions: boolean;
    decisionTypes: string[];
    confidence: number;
    reasoning: string;
  };
}

// Define the initial state using that type
const initialState: ResearchSessionState = {
  sessionId: null,
  chatHistory: chatHistoryAdapter.getInitialState(), // Use adapter's initial state
  streamingDocument: '',
  currentQuery: null,
  currentChatId: null,

  // ✅ NEW: Progressive loading states (Phase 4)
  isInitializing: false,
  isChatHistoryLoading: false,
  historyBatchSize: 5,

  // ✅ NEW: Cursor-based pagination states (Faster Historical Chat Loading)
  hasMoreMessages: false,
  nextCursor: null,
  isLoadingMore: false,

  streaming: false,
  queryAnalyzing: false,
  enhanceQueryAnalyzing: '',
  searchRelatedResults: false,
  filteringLegislationStatus: null,
  filterOutputStreaming: '',
  refiningLegislationStatus: null,
  refinementOutputStreaming: '',
  refinementThoughts: {
    thoughts: '',
  },
  thoughtsStreaming: false,
  analyseLegislation: [],
  notification: null, // Changed from error: null
  isCancelled: false,
  currentResponseSourceType: null, // Issue #852: Reset on stream start
  isWaitingForFallback: false, // Issue #852: Reset on stream start
  streamingMessageId: null, // Issue #852: Unified Streaming Model
  executedStagesQueue: [], // Timeline stages tracking
  translatingMessageIds: [], // Issue #886: Messages being translated
  sessionFilters: null, // Session-scoped filter isolation
  autoFilterLoading: false, // Issue #886: Session-scoped smart filter loading
  documentExtracting: false, // Document analysis: shimmer during PDF extraction
  extractionProgressMessage: null,
  identifyingIssues: false, // Document analysis: shimmer during issue identification
  discoveredIssues: [], // Issues found live during identification (shown in shimmer)
  comparingLegislation: false, // Document analysis: shimmer during comparative analysis phase
  comparisonCount: 0, // How many comparison_result events have arrived
  discoveredComparisons: [], // Live comparison results shown in shimmer
  pendingComparisonResults: [], // Full ComparisonResult objects accumulated during comparative_analysis
  extractionBatchSubText: null, // Research extraction: per-batch progress sub-text
  reactAgentToolName: null, // ReAct agent: current tool being executed
  reactAgentSubText: null, // ReAct agent: contextual shimmer sub-text (query, doc count, or reflection)
  iterationTransition: null, // ReAct agent: between-iteration reflection state (#2327)
  uriMap: {}, // URI short-code mapping for progressive link rendering during streaming
  hasUriMap: false,
  webSourcesMap: {}, // #3817: rich web grounding source info for hover-card chips
  temporalVerifierBySession: {}, // #5069: per-session temporal verifier flags
  semanticVerifierBySession: {}, // #5069: per-session semantic verifier flags
  authorityVerifierBySession: {}, // #5773: per-session authority verifier advisory
  contextChipSkippedBySession: {}, // #8095: per-session skipped context chips
  contextScopeChangedBySession: {}, // per-session context-scope-change step
};

const researchSessionSlice = createSlice({
  name: 'researchSession',
  initialState,
  reducers: {
    initializeSession: (
      state,
      action: PayloadAction<{
        sessionId: string;
        initialQuery?: string;
        initialMessages?: ChatHistoryItem[];
        // When true, sets streaming=true immediately to show shimmer text
        // Used when returning to a session that has an active/pending task
        needsReconnection?: boolean;
      }>
    ) => {
      state.sessionId = action.payload.sessionId;
      // Issue #7155 H2: the editor document lane belongs to ONE session —
      // without this, SPA-navigating from a finished doc-gen session leaks
      // its full document into the next session's editor (and reconnects
      // append onto it).
      state.streamingDocument = '';
      if (action.payload.initialQuery) {
        state.currentQuery = action.payload.initialQuery;
      }
      if (action.payload.initialMessages) {
        // Use adapter's setAll to replace all messages
        chatHistoryAdapter.setAll(
          state.chatHistory,
          action.payload.initialMessages
        );
      } else {
        // Clear history if no initial messages are provided
        chatHistoryAdapter.removeAll(state.chatHistory);
      }
      // If needsReconnection is true, set streaming=true immediately
      // This ensures shimmer text is visible while SSE reconnects.
      // We set queryAnalyzing=true as a generic "reconnecting" indicator —
      // the next SSE event will update to the actual stage.
      state.streaming = action.payload.needsReconnection ?? false;
      state.queryAnalyzing = action.payload.needsReconnection ?? false;
      if (!action.payload.needsReconnection) {
        // Only clear shimmer flags when NOT reconnecting.
        // During reconnection, keep them so the shimmer persists until
        // SSE replays catch up to the current pipeline stage.
        state.enhanceQueryAnalyzing = '';
        state.searchRelatedResults = false;
        state.filteringLegislationStatus = null;
        state.filterOutputStreaming = '';
        state.refiningLegislationStatus = null;
        state.refinementOutputStreaming = '';
        state.refinementThoughts = {
          thoughts: '',
        };
        state.thoughtsStreaming = false;
        state.analyseLegislation = [];
      }
      state.notification = null; // Clear notification on init
      // ✅ Reset pagination state for new session
      state.hasMoreMessages = false;
      state.nextCursor = null;
      state.isLoadingMore = false;
    },
    addMessage: (state, action: PayloadAction<ChatHistoryItem>) => {
      // Use adapter's setOne for upsert (prevents duplicates from SSE replay)
      chatHistoryAdapter.setOne(state.chatHistory, action.payload);
      // Issue #852: Log message addition for debugging two-message flow
      // ✅ Invalidate cache when message is added
      if (state.sessionId) {
        invalidateChatHistoryCache(state.sessionId);
      }
    },
    addMessageBatch: (state, action: PayloadAction<ChatHistoryItem[]>) => {
      // ✅ Batch entity adapter updates for performance
      // Use setAll for initial load (empty state), upsertMany for additions
      // This is more efficient than addMany which triggers sorting per item
      if (state.chatHistory.ids.length === 0) {
        chatHistoryAdapter.setAll(state.chatHistory, action.payload);
      } else {
        chatHistoryAdapter.upsertMany(state.chatHistory, action.payload);
      }
      // ✅ Invalidate cache when batch is added
      if (state.sessionId) {
        invalidateChatHistoryCache(state.sessionId);
      }
    },
    updateMessage: (
      state,
      action: PayloadAction<{ id: string; changes: Partial<ChatHistoryItem> }>
    ) => {
      // Add updateMessage action using adapter's updateOne
      chatHistoryAdapter.updateOne(state.chatHistory, action.payload);
      // ✅ Invalidate cache when message is updated
      if (state.sessionId) {
        invalidateChatHistoryCache(state.sessionId);
      }
    },
    removeMessage: (state, action: PayloadAction<string>) => {
      // Add removeMessage action using adapter's removeOne
      chatHistoryAdapter.removeOne(state.chatHistory, action.payload);
      // ✅ Invalidate cache when message is removed
      if (state.sessionId) {
        invalidateChatHistoryCache(state.sessionId);
      }
    },
    /**
     * Update the latest optimistic user message with db_id from server.
     * Called when query_analysis_start receives chatId.
     *
     * This fixes the chat ordering bug where:
     * 1. User submits query → user message added without db_id
     * 2. SSE starts → assistant message added with db_id
     * 3. Sort comparator couldn't properly order them
     *
     * By updating the user message with db_id immediately when server sends it,
     * both user and assistant messages have db_id and sort correctly by db_id.
     */
    updateOptimisticUserMessageDbId: (
      state,
      action: PayloadAction<{ chatId: number }>
    ) => {
      const { chatId } = action.payload;

      // Find the latest user message without db_id (the optimistic one)
      // Messages are sorted by time, so we iterate in reverse to find the latest
      const userMessageWithoutDbId = [...state.chatHistory.ids]
        .reverse()
        .map((id) => state.chatHistory.entities[id])
        .find((msg) => msg && msg.role === 'user' && msg.db_id === undefined);

      if (userMessageWithoutDbId) {
        // Update the message with the db_id from server
        chatHistoryAdapter.updateOne(state.chatHistory, {
          id: userMessageWithoutDbId.id,
          changes: { db_id: chatId },
        });
        // Invalidate cache since message was updated
        if (state.sessionId) {
          invalidateChatHistoryCache(state.sessionId);
        }
      } else {
        console.warn(
          '⚠️ [Redux] updateOptimisticUserMessageDbId: No optimistic user message found to update'
        );
      }
    },
    setChatHistoryLoading: (state, action: PayloadAction<boolean>) => {
      // ✅ NEW: Track progressive history batch loading
      state.isChatHistoryLoading = action.payload;
    },
    setInitializing: (state, action: PayloadAction<boolean>) => {
      // ✅ NEW: Track initial session/history load
      state.isInitializing = action.payload;
    },
    clearNotification: (state) => {
      // Renamed from clearError
      state.notification = null;
    },
    setNotification: (state, action: PayloadAction<Notification | null>) => {
      // New action to set any notification
      state.notification = action.payload;
    },
    setStreamStatus: (
      state,
      action: PayloadAction<
        Partial<
          Pick<
            ResearchSessionState,
            | 'streaming'
            | 'queryAnalyzing'
            | 'searchRelatedResults'
            | 'filteringLegislationStatus'
            | 'refiningLegislationStatus'
            | 'analyseLegislation'
            | 'documentExtracting'
            | 'extractionProgressMessage'
            | 'identifyingIssues'
            | 'comparingLegislation'
            | 'extractionBatchSubText'
            | 'reactAgentToolName'
            | 'reactAgentSubText'
            | 'iterationTransition'
            // Removed 'error' as it's handled by 'notification' now
          >
        > & { notification?: Notification | null } // Allow setting notification here too
      >
    ) => {
      const { notification, ...otherStatus } = action.payload;
      Object.assign(state, otherStatus);
      if (notification !== undefined) {
        // Check if notification was explicitly passed
        state.notification = notification;
      }
    },
    /**
     * Issue #886: Session-scoped smart filter loading state
     * Each session tracks its own loading state independently to prevent
     * cross-session contamination (spinning wheel in other sessions)
     */
    setAutoFilterLoading: (state, action: PayloadAction<boolean>) => {
      state.autoFilterLoading = action.payload;
    },
    appendEnhanceQuery: (state, action: PayloadAction<string>) => {
      state.enhanceQueryAnalyzing += action.payload;
    },
    setEnhanceQuery: (state, action: PayloadAction<string>) => {
      state.enhanceQueryAnalyzing = action.payload;
    },
    addAnalyseLegislation: (state, action: PayloadAction<string>) => {
      state.analyseLegislation.push(action.payload);
    },
    setAnalyseLegislation: (state, action: PayloadAction<string[]>) => {
      state.analyseLegislation = action.payload;
    },
    appendRefinementOutput: (state, action: PayloadAction<string>) => {
      state.refinementOutputStreaming += action.payload;
    },
    setRefinementOutput: (state, action: PayloadAction<string>) => {
      state.refinementOutputStreaming = action.payload;
    },
    appendRefinementThoughts: (state, action: PayloadAction<string>) => {
      state.refinementThoughts.thoughts += action.payload;
    },
    setRefinementThoughts: (state, action: PayloadAction<string>) => {
      state.refinementThoughts.thoughts = action.payload;
    },
    /**
     * Issue #886: Set translated thoughts on a message
     * Called when backend sends thoughts_translated SSE event with Slovak translation
     * Updates message.thinking, executedStages thoughts, and removes from translatingMessageIds
     */
    setTranslatedThoughts: (
      state,
      action: PayloadAction<{ chatId: number; translatedThoughts: string }>
    ) => {
      const { chatId, translatedThoughts } = action.payload;
      const messageId = `assistant-${chatId}`;

      // Update refinementThoughts buffer with translated text
      state.refinementThoughts.thoughts = translatedThoughts;

      // Update the message's thinking field with Slovak translation
      const message = state.chatHistory.entities[messageId];
      if (message) {
        message.thinking = translatedThoughts;

        // Issue #886: Also update executedStages if they have embedded thoughts
        // This ensures stage.thoughts displays translated content in the timeline
        if (message.executedStages && message.executedStages.length > 0) {
          message.executedStages = message.executedStages.map((stage) => {
            // Only update stages that have thoughts (refiningLegislationStatus, webSearchFallback)
            if (
              (stage.stageKey === 'refiningLegislationStatus' ||
                stage.stageKey === 'webSearchFallback') &&
              stage.thoughts
            ) {
              return { ...stage, thoughts: translatedThoughts };
            }
            return stage;
          });
        }
      }

      // Remove from translating list (shimmer will stop)
      state.translatingMessageIds = state.translatingMessageIds.filter(
        (id) => id !== messageId
      );
    },
    setThoughtsStreaming: (state, action: PayloadAction<boolean>) => {
      state.thoughtsStreaming = action.payload;
    },
    appendFilterOutput: (state, action: PayloadAction<string>) => {
      state.filterOutputStreaming += action.payload;
    },
    setFilterOutput: (state, action: PayloadAction<string>) => {
      state.filterOutputStreaming = action.payload;
    },
    clearStreamOutputs: (state) => {
      state.filterOutputStreaming = '';
      state.refinementOutputStreaming = '';
      state.enhanceQueryAnalyzing = '';
      state.refinementThoughts = {
        thoughts: '',
      };
      state.thoughtsStreaming = false;
      state.currentResponseSourceType = null; // Issue #852: Reset on stream start
      state.isWaitingForFallback = false; // Issue #852: Reset on stream start
    },
    // Issue #852: Set current response source type during streaming
    setCurrentResponseSourceType: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.currentResponseSourceType = action.payload;
    },
    // Issue #852: Set waiting for fallback state (web search after document search)
    setIsWaitingForFallback: (state, action: PayloadAction<boolean>) => {
      state.isWaitingForFallback = action.payload;
    },
    // URI short-code mapping for progressive link rendering during streaming
    setUriMap: (state, action: PayloadAction<Record<string, string>>) => {
      state.uriMap = action.payload;
      state.hasUriMap = Object.keys(action.payload).length > 0;
    },

    // #3817: rich web grounding source info — merge so multi-iteration
    // web_search calls accumulate (the backend emits the union once at the
    // end, but a re-emit on resume should not clobber prior info).
    setWebSourcesMap: (
      state,
      action: PayloadAction<Record<string, WebSourceInfo>>
    ) => {
      state.webSourcesMap = { ...state.webSourcesMap, ...action.payload };
    },

    // #5069: Store temporal verifier result for a session.
    // Called when the `temporal_verifier_flag` SSE event arrives (at most once
    // per answer). Overwrites any previous result for that session.
    setTemporalVerifierForSession: (
      state,
      action: PayloadAction<{
        sessionId: string;
        result: TemporalVerifierResult;
      }>
    ) => {
      state.temporalVerifierBySession[action.payload.sessionId] =
        action.payload.result;
    },

    // #5069: Clear temporal verifier result for a session.
    // Called at stream start so a clean later answer in the same session does
    // not show a stale panel (the event fires only when flags exist; silence
    // means no flags — clear the previous result to reflect that).
    clearTemporalVerifierForSession: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      delete state.temporalVerifierBySession[action.payload.sessionId];
    },

    // #5069: Store semantic verifier result (faithfulness + scope) for a session.
    // Called when the `semantic_verifier_flag` SSE event arrives (at most once per
    // answer). Overwrites any previous result for that session.
    setSemanticVerifierForSession: (
      state,
      action: PayloadAction<{
        sessionId: string;
        result: SemanticVerifierResult;
      }>
    ) => {
      state.semanticVerifierBySession[action.payload.sessionId] =
        action.payload.result;
    },

    // #5069: Clear semantic verifier result for a session.
    // Called at stream start so a clean later answer in the same session does not
    // show a stale panel (the event fires only when flags exist; silence means no
    // flags — clear the previous result to reflect that).
    clearSemanticVerifierForSession: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      delete state.semanticVerifierBySession[action.payload.sessionId];
    },

    // #5773: Store authority verifier result for a session.
    // Called when the `authority_verifier_flag` SSE event arrives (at most once per
    // answer). Overwrites any previous result for that session.
    setAuthorityVerifierForSession: (
      state,
      action: PayloadAction<{
        sessionId: string;
        result: AuthorityVerifierResult;
      }>
    ) => {
      state.authorityVerifierBySession[action.payload.sessionId] =
        action.payload.result;
    },

    // #5773: Clear authority verifier result for a session.
    // Called at stream start so a clean later answer in the same session does not
    // show a stale panel (the event fires only when the gap is detected).
    clearAuthorityVerifierForSession: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      delete state.authorityVerifierBySession[action.payload.sessionId];
    },

    // #8095: Store skipped-context-chip result for a session.
    // Called when the `context_chip_skipped` SSE event arrives (at most once per
    // request). Overwrites any previous result for that session.
    setContextChipSkippedForSession: (
      state,
      action: PayloadAction<{
        sessionId: string;
        result: ContextChipSkippedResult;
      }>
    ) => {
      state.contextChipSkippedBySession[action.payload.sessionId] =
        action.payload.result;
    },

    // #8095: Clear skipped-context-chip result for a session.
    // Called at stream start so a clean later request in the same session does
    // not show a stale notice (the event fires only when warnings exist;
    // silence means the whole selection resolved).
    clearContextChipSkippedForSession: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      delete state.contextChipSkippedBySession[action.payload.sessionId];
    },

    // Store the context-scope-change result for a session. Called when the
    // `context_scope_changed` SSE event arrives (at most once per request).
    // Overwrites any previous result for that session.
    setContextScopeChangedForSession: (
      state,
      action: PayloadAction<{
        sessionId: string;
        result: ContextScopeChangedResult;
      }>
    ) => {
      state.contextScopeChangedBySession[action.payload.sessionId] =
        action.payload.result;
    },

    // Clear the context-scope-change result for a session. Called at stream
    // start so a clean later request in the same session does not show a stale
    // notice (the event fires only when the scope actually changed).
    clearContextScopeChangedForSession: (
      state,
      action: PayloadAction<{ sessionId: string }>
    ) => {
      delete state.contextScopeChangedBySession[action.payload.sessionId];
    },

    // ========================================
    // Issue #852: Unified Streaming Model Actions
    // ========================================

    /**
     * Start streaming a new message - adds to chatHistory with isStreaming=true
     * Called when:
     * - refining_legislation_start: Start Message 1 (document search)
     * - fallback_message_start: Start Message 2 (web search fallback)
     */
    startStreamingMessage: (
      state,
      action: PayloadAction<{
        id: string;
        db_id?: number | undefined;
        role: 'user' | 'assistant';
        response_source_type?: ResponseSourceType | undefined;
        has_document_backing?: boolean | undefined;
        used_web_search?: boolean | undefined;
        used_document_analysis?: boolean | undefined;
        parent_message_id?: number | undefined; // Link to Message 1 for fallback messages
        document_analysis_data?: DocumentAnalysisData | undefined; // Progressive doc analysis
        /** Issue #5862: web source authority gate from SSE start event. */
        web_sources_all_official?: boolean | null | undefined;
        /** Issue #5948/#5951: source-class v2 signals from SSE start event. */
        web_official_class?: string | null | undefined;
        db_backing_class?: string | null | undefined;
        /** Issue #5950: legal-state date the answer reflects, from SSE start event. */
        legal_state_date?: string | null | undefined;
      }>
    ) => {
      const message: ChatHistoryItem = {
        id: action.payload.id,
        db_id: action.payload.db_id,
        created_at: new Date().toISOString(),
        content: '', // Empty, will be appended
        role: action.payload.role,
        data_sources: [],
        has_document_backing: action.payload.has_document_backing ?? true,
        response_source_type:
          action.payload.response_source_type ?? 'document_search',
        used_web_search: action.payload.used_web_search,
        used_document_analysis: action.payload.used_document_analysis,
        web_sources_all_official:
          action.payload.web_sources_all_official ?? undefined,
        web_official_class: action.payload.web_official_class ?? undefined,
        db_backing_class: action.payload.db_backing_class ?? undefined,
        legal_state_date: action.payload.legal_state_date ?? undefined,
        isStreaming: true,
        parent_message_id: action.payload.parent_message_id,
        document_analysis_data: action.payload.document_analysis_data,
      };
      chatHistoryAdapter.setOne(state.chatHistory, message);
      state.streamingMessageId = action.payload.id;
      // Invalidate cache so navigation-based recovery gets fresh data
      if (state.sessionId) {
        invalidateChatHistoryCache(state.sessionId);
      }
    },

    /**
     * Append content to the streaming message
     * Called on each SSE chunk (refinement_output_streaming, fallback_output_streaming)
     */
    appendToStreamingMessage: (state, action: PayloadAction<string>) => {
      if (state.streamingMessageId) {
        const message = state.chatHistory.entities[state.streamingMessageId];
        if (message) {
          // Content stays raw (with short codes) — decode happens at render
          // time in StreamingTypingText to keep typing animation positions stable.
          message.content += action.payload;
        }
      }
    },

    /**
     * Replace streaming message content (complete frame)
     * Called on refinement_output_complete — decoded + hallucination-corrected text
     * replaces the streaming preview that contained short URI codes
     */
    replaceStreamingMessageContent: (state, action: PayloadAction<string>) => {
      if (state.streamingMessageId) {
        const message = state.chatHistory.entities[state.streamingMessageId];
        if (message) {
          message.content = action.payload;
        }
      }
    },

    /**
     * Complete streaming - sets isStreaming=false and clears streamingMessageId
     * Called when:
     * - fallback_message_start: Complete Message 1 before starting Message 2
     * - finish_research_end: Complete the final message
     * - fallback_message_end: Complete fallback message
     */
    completeStreamingMessage: (
      state,
      action: PayloadAction<{
        data_sources?: DataSourceInput[];
        has_document_backing?: boolean;
        response_source_type?: ResponseSourceType;
        /** Issue #886: If true, mark message for translation immediately to show shimmer */
        willTranslateThoughts?: boolean;
      }>
    ) => {
      if (state.streamingMessageId) {
        const messageId = state.streamingMessageId;
        const message = state.chatHistory.entities[messageId];
        if (message) {
          message.isStreaming = false;
          if (action.payload.data_sources) {
            message.data_sources = action.payload.data_sources;
          }
          if (action.payload.has_document_backing !== undefined) {
            message.has_document_backing = action.payload.has_document_backing;
          }
          if (
            action.payload.response_source_type &&
            isValidResponseSourceType(action.payload.response_source_type)
          ) {
            message.response_source_type = action.payload.response_source_type;
          }
          // Issue #886: Only copy thoughts if NOT waiting for translation
          // When willTranslateThoughts is true, backend will send thoughts_translated SSE
          // with Slovak translation - we'll update message.thinking then
          if (
            state.refinementThoughts.thoughts &&
            state.refinementThoughts.thoughts.length > 0 &&
            !action.payload.willTranslateThoughts
          ) {
            message.thinking = state.refinementThoughts.thoughts;
          }
          // Copy executed stages timeline to the message for post-completion display
          if (state.executedStagesQueue.length > 0) {
            message.executedStages = [...state.executedStagesQueue];
          }
          // For doc analysis: persist streamed summary into document_analysis_data.summary
          // so DocumentAnalysisChatMessage can render it after isStreaming flips to false
          // (without a page refresh to load it from DB).
          if (message.document_analysis_data && message.content) {
            message.document_analysis_data.summary = message.content;
          }
          // Issue #886: Mark message as waiting for translation (shows shimmer)
          if (action.payload.willTranslateThoughts) {
            if (!state.translatingMessageIds.includes(messageId)) {
              state.translatingMessageIds.push(messageId);
            }
          }
        }
      }
      state.streamingMessageId = null;
      // Invalidate cache so navigation-based recovery gets fresh data
      if (state.sessionId) {
        invalidateChatHistoryCache(state.sessionId);
      }
    },

    /** Issue #7155: append a document_output_streaming chunk (editor lane). */
    appendStreamingDocument: (state, action: PayloadAction<string>) => {
      state.streamingDocument += action.payload;
    },

    /** Issue #7155: reset the live document lane (stream start / reconciliation). */
    clearStreamingDocument: (state) => {
      state.streamingDocument = '';
    },

    /**
     * Issue #7155 Option A: authoritative doc-gen reconciliation. During a
     * document_generation turn the summary streams into the chat bubble and
     * the document streams into the `streamingDocument` editor lane; the
     * `document_generation_complete` SSE event then delivers the persisted
     * document (emitted only AFTER the backend saved it — DB is the source of
     * truth). Pin the lane to that persisted content so the live view matches
     * a page reload, and retype the message
     * `document_generation`/`document_revision` so the artifact chip renders
     * and DocumentGenerationContent routes the lane into the editor panel.
     *
     * Target resolution: by DB chat id first (`assistant-<chat_id>` is the id
     * startStreamingMessage assigns; db_id covers renamed entries), then the
     * active streaming message, then the newest assistant message — the event
     * can arrive after fallback_message_end already cleared streamingMessageId.
     */
    applyDocumentGenerationResult: (
      state,
      action: PayloadAction<{
        chatId: number | null;
        content: string;
        responseSourceType: 'document_generation' | 'document_revision';
        /**
         * Issue #7155: whether retrieved corpus sources ground this draft —
         * mirrors the persisted message flag (fallback_message_end's generic
         * handler mistyped it, and reload reads the DB value). Defaults to
         * grounded when the event predates the field.
         */
        hasDocumentBacking?: boolean | undefined;
        /** Issue #7155: artifact reference persisted for this turn. */
        versionNumber?: number | null;
      }>
    ) => {
      const {
        chatId,
        content,
        responseSourceType,
        hasDocumentBacking,
        versionNumber,
      } = action.payload;
      let target: ChatHistoryItem | undefined;
      if (chatId !== null) {
        target =
          state.chatHistory.entities[`assistant-${chatId}`] ??
          Object.values(state.chatHistory.entities).find(
            (m) => m?.db_id === chatId && m.role === 'assistant'
          );
      }
      if (!target && state.streamingMessageId) {
        target = state.chatHistory.entities[state.streamingMessageId];
      }
      if (!target) {
        for (let i = state.chatHistory.ids.length - 1; i >= 0; i--) {
          const candidate =
            state.chatHistory.entities[state.chatHistory.ids[i] as string];
          if (candidate?.role === 'assistant') {
            target = candidate;
            break;
          }
        }
      }
      // Option A: the payload content is the DOCUMENT (editor lane) — the chat
      // bubble keeps the streamed summary, so message content is NOT touched.
      // Replace the live lane with the authoritative persisted document; the
      // editor follows this field for both the live typing and the final state.
      state.streamingDocument = content;
      if (!target) return;
      target.response_source_type = responseSourceType;
      target.has_document_backing = hasDocumentBacking ?? true;
      target.isStreaming = false;
      if (typeof versionNumber === 'number') {
        target.document_version_number = versionNumber;
      }
    },

    /** Update status of the current streaming message's document_analysis_data.
     *  Drives phase-aware inline loading text in DocumentAnalysisChatMessage.
     *  Called at: issue_identification_end → 'issues_identified'
     *             comparative_analysis_end → 'comparison_complete'
     *             document_analysis_summary_end → 'complete' (via completeStreamingMessage)
     */
    setDocAnalysisStatus: (
      state,
      action: PayloadAction<DocumentAnalysisData['status']>
    ) => {
      if (state.streamingMessageId) {
        const message = state.chatHistory.entities[state.streamingMessageId];
        if (message?.document_analysis_data) {
          message.document_analysis_data.status = action.payload;
        }
      }
    },

    /**
     * Document analysis: accumulate a discovered issue in the shimmer area.
     * Called for each `issue_identified` SSE event BEFORE the streaming message
     * is created. Issues are shown live in StreamingSection, then moved to the
     * structured message all at once on `issue_identification_end`.
     */
    addDiscoveredIssue: (state, action: PayloadAction<LegalIssue>) => {
      state.discoveredIssues.push(action.payload);
    },

    /** Clear discovered issues after they've been moved into the streaming message. */
    clearDiscoveredIssues: (state) => {
      state.discoveredIssues = [];
    },

    /** Append a comparison result to the live shimmer feed during comparative_analysis. */
    addDiscoveredComparison: (
      state,
      action: PayloadAction<{
        issue_id: string;
        compliance_status: string;
        document_clause: string;
      }>
    ) => {
      state.discoveredComparisons.push(action.payload);
    },

    /** Clear comparison shimmer feed on comparative_analysis_end. */
    clearDiscoveredComparisons: (state) => {
      state.discoveredComparisons = [];
    },

    /**
     * Bulk-copy discovered issues into the streaming message's identified_issues.
     * Called at issue_identification_end. Replaces the array reference so selectors
     * detect the change and React re-renders.
     */
    syncDiscoveredIssuesToStreamingMessage: (state) => {
      if (state.streamingMessageId && state.discoveredIssues.length > 0) {
        const message = state.chatHistory.entities[state.streamingMessageId];
        if (message?.document_analysis_data) {
          message.document_analysis_data.identified_issues =
            state.discoveredIssues.slice();
        }
      } else {
      }
    },

    /** Accumulate a full ComparisonResult in the pending array during comparative_analysis. */
    addPendingComparison: (state, action: PayloadAction<ComparisonResult>) => {
      state.pendingComparisonResults.push(action.payload);
    },

    /** Clear pending comparisons after bulk-syncing to the streaming message. */
    clearPendingComparisons: (state) => {
      state.pendingComparisonResults = [];
    },

    /**
     * Bulk-copy pending comparisons into the streaming message's comparison_results.
     * Called at comparative_analysis_end. Replaces the array reference so selectors
     * detect the change and React re-renders.
     */
    syncPendingComparisonsToStreamingMessage: (state) => {
      if (
        state.streamingMessageId &&
        state.pendingComparisonResults.length > 0
      ) {
        const message = state.chatHistory.entities[state.streamingMessageId];
        if (message?.document_analysis_data) {
          message.document_analysis_data.comparison_results =
            state.pendingComparisonResults.slice();
        }
      } else {
      }
    },

    /** Increment comparison counter as each comparison_result SSE event arrives. */
    incrementComparisonCount: (state) => {
      state.comparisonCount += 1;
    },

    /** Reset comparison counter (called on comparative_analysis_start). */
    resetComparisonCount: (state) => {
      state.comparisonCount = 0;
    },

    setCurrentQuery: (state, action: PayloadAction<string>) => {
      state.currentQuery = action.payload;
    },
    setCurrentChatId: (state, action: PayloadAction<number | null>) => {
      state.currentChatId = action.payload;

      // Persist to localStorage with session-specific key (if sessionId exists)
      if (state.sessionId) {
        persistChatId(state.sessionId, action.payload);
      }
    },

    // Set only the sessionId without touching any other state.
    // Dispatched at the very start of initializeSessionData so that isCurrentSession()
    // guards in SSE handlers return true even if the user submits a query before
    // the async fetchChatHistory call completes (race-condition fix).
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    streamStart: (state) => {
      state.streaming = true;
      state.notification = null; // Clear notification on stream start
      state.isCancelled = false;
      state.currentChatId = null; // Reset chat_id for new research
      state.filterOutputStreaming = '';
      state.refinementOutputStreaming = '';
      state.enhanceQueryAnalyzing = '';
      state.refinementThoughts = {
        thoughts: '',
      };
      state.thoughtsStreaming = false;
      state.analyseLegislation = [];
      state.extractionBatchSubText = null;
      state.queryAnalyzing = false;
      state.searchRelatedResults = false;
      state.filteringLegislationStatus = null;
      state.refiningLegislationStatus = null;
      state.executedStagesQueue = []; // Clear timeline for new stream
      state.uriMap = {}; // Clear URI map for new stream (uri_map SSE event will repopulate)
      state.hasUriMap = false;
      state.webSourcesMap = {}; // #3817: clear web sources map for new stream
    },

    /**
     * Add an executed stage to the timeline queue.
     * Called when each SSE stage event is received.
     * Issue #886: Now accepts optional metadata (smartFilter, documentCount, thoughts).
     */
    addExecutedStage: (
      state,
      action: PayloadAction<ShimmerStageKey | Omit<ExecutedStage, 'timestamp'>>
    ) => {
      const payload = action.payload;
      // Support both simple stageKey string and full metadata object
      if (typeof payload === 'string') {
        state.executedStagesQueue.push({
          stageKey: payload,
          timestamp: Date.now(),
        });
      } else {
        state.executedStagesQueue.push({
          ...payload,
          timestamp: Date.now(),
        });
      }
    },

    /**
     * Update an existing executed stage with additional metadata.
     * Issue #886: Used when metadata arrives after the stage was initially added.
     */
    updateExecutedStage: (
      state,
      action: PayloadAction<{
        stageKey: ShimmerStageKey;
        updates: Partial<Omit<ExecutedStage, 'stageKey' | 'timestamp'>>;
      }>
    ) => {
      const { stageKey, updates } = action.payload;
      // findLastIndex: when multiple stages share a key (e.g. reactAgentIteration),
      // always update the most recently added one.
      const stageIndex = state.executedStagesQueue.findLastIndex(
        (s) => s.stageKey === stageKey
      );
      if (stageIndex !== -1) {
        const existingStage = state.executedStagesQueue[stageIndex];
        if (existingStage) {
          state.executedStagesQueue[stageIndex] = {
            ...existingStage,
            ...updates,
          };
        }
      }
    },

    /**
     * Clear executed stages queue - used when starting a fallback message
     * to prevent fallback from inheriting stages from the primary message.
     * Issue #886: Fallback messages should have their own timeline (webSearchFallback only).
     */
    clearExecutedStagesQueue: (state) => {
      state.executedStagesQueue = [];
    },

    // ========================================
    // Session-Scoped Filter Actions
    // ========================================
    /**
     * Freeze filters for the current session when a search/research starts.
     * This captures the current filter state and isolates it from changes
     * made in other sessions or by other smart filter events.
     */
    freezeSessionFilters: (
      state,
      action: PayloadAction<{ filters: Filters }>
    ) => {
      state.sessionFilters = {
        original: action.payload.filters,
        frozenAt: Date.now(),
      };
    },

    /**
     * Apply AI smart filter suggestions to the SESSION-SCOPED filters.
     * This does NOT affect global filters - only this session's view.
     * Other sessions remain unaffected.
     */
    applySessionSmartFilters: (
      state,
      action: PayloadAction<{
        searchLegislation: boolean;
        searchDecisions: boolean;
        decisionTypes: string[];
        confidence: number;
        reasoning: string;
        // Temporal fields from smart filter
        effectiveAsOf?: string | null;
        effectivePeriodStart?: string | null;
        effectivePeriodEnd?: string | null;
        temporalReasoning?: string | null;
        // AI actions — filter changes for disclaimer UI
        changes?: Array<{
          field: string;
          from: string;
          to: string;
          applied: boolean;
          reason: string;
        }> | null;
      }>
    ) => {
      if (!state.sessionFilters) {
        console.warn(
          '⚠️ [Redux] applySessionSmartFilters - No session filters frozen, ignoring'
        );
        return;
      }

      const suggestion = action.payload;
      const original = state.sessionFilters.original;

      // Create afterSmartFilter by applying narrowing to the original
      // Overlay logic: AI can only narrow, not expand
      const afterSmartFilter: Filters = {
        // If user had searchLegislation=false, keep it false even if AI suggests true
        searchLegislation: original.searchLegislation
          ? suggestion.searchLegislation
          : false,
        searchDecisions: original.searchDecisions
          ? suggestion.searchDecisions
          : false,
        decisions: original.decisions ? { ...original.decisions } : undefined,
        legislation: original.legislation
          ? { ...original.legislation }
          : undefined,
      };

      // Apply decision type narrowing if decisions are being searched
      if (
        afterSmartFilter.searchDecisions &&
        afterSmartFilter.decisions &&
        suggestion.decisionTypes.length > 0
      ) {
        afterSmartFilter.decisions.decisionTypes = suggestion.decisionTypes.map(
          (id) => ({
            id,
            name: getDecisionTypeLabelStatic(id),
          })
        );
      }

      // Apply temporal filter narrowing if smart filter detected a date
      // Only applies when user hasn't manually set effectiveAsOf (manual = priority)
      if (suggestion.effectiveAsOf && afterSmartFilter.legislation) {
        const currentEffective = original.legislation?.effectiveAsOf;
        // "today" is the default sentinel — safe to override
        if (!currentEffective || currentEffective === 'today') {
          afterSmartFilter.legislation = {
            ...afterSmartFilter.legislation,
            effectiveAsOf: suggestion.effectiveAsOf,
            activeOnly: true,
          };
        }
      }

      state.sessionFilters.afterSmartFilter = afterSmartFilter;
      state.sessionFilters.smartFilterApplied = {
        searchLegislation: suggestion.searchLegislation,
        searchDecisions: suggestion.searchDecisions,
        decisionTypes: suggestion.decisionTypes,
        confidence: suggestion.confidence,
        reasoning: suggestion.reasoning,
        // Temporal fields
        ...(suggestion.effectiveAsOf && {
          effectiveAsOf: suggestion.effectiveAsOf,
          effectivePeriodStart: suggestion.effectivePeriodStart ?? undefined,
          effectivePeriodEnd: suggestion.effectivePeriodEnd ?? undefined,
          temporalReasoning: suggestion.temporalReasoning ?? undefined,
        }),
        // AI actions — store filter changes for SmartFilterDisclaimer
        ...(suggestion.changes && { changes: suggestion.changes }),
      };
    },

    /**
     * Clear session filters when research completes or session changes.
     */
    clearSessionFilters: (state) => {
      state.sessionFilters = null;
    },

    /**
     * Add a complete search results message to chat history.
     * Used by search_documents flow where results arrive as a single SSE event
     * (not progressively streamed like text). Creates a complete, non-streaming message.
     */
    addSearchResultsMessage: (
      state,
      action: PayloadAction<{
        chatId: number;
        searchResultsData: import('@/lib/redux/slices/search').SearchResultItem[];
        searchMetadata: import('@/types/researchTypes').SearchResultsMetadata;
      }>
    ) => {
      const { chatId, searchResultsData, searchMetadata } = action.payload;
      const messageId = `assistant-${chatId}`;

      const message: ChatHistoryItem = {
        id: messageId,
        db_id: chatId,
        created_at: new Date().toISOString(),
        content: '', // No text content — rendered as interactive search results
        role: 'assistant',
        has_document_backing: true,
        response_source_type: 'search_documents',
        isStreaming: false, // Complete message, not streaming
        search_results_data: searchResultsData,
        search_metadata: searchMetadata,
        executedStages:
          state.executedStagesQueue.length > 0
            ? [...state.executedStagesQueue]
            : undefined,
      };
      chatHistoryAdapter.setOne(state.chatHistory, message);
      state.streamingMessageId = null;

      // Invalidate cache when search results message is added
      if (state.sessionId) {
        invalidateChatHistoryCache(state.sessionId);
      }
    },

    streamEnd: (
      state,
      // Payload can now be a more structured error/warning or undefined
      action: PayloadAction<{ notification?: Notification } | undefined>
    ) => {
      state.streaming = false;
      state.queryAnalyzing = false; // Safety: clear in case search_page_results else-branch ran without clearing it
      state.extractionBatchSubText = null;
      state.reactAgentToolName = null;
      state.reactAgentSubText = null;
      state.iterationTransition = null;
      state.autoFilterLoading = false; // Safety: clear in case search phase never fired
      if (action.payload?.notification) {
        state.notification = action.payload.notification;
      }
    },

    streamCancelled: (state) => {
      state.streaming = false;
      state.isCancelled = true;
      state.queryAnalyzing = false;
      state.extractionBatchSubText = null;
      state.reactAgentToolName = null;
      state.reactAgentSubText = null;
      state.iterationTransition = null;
      state.autoFilterLoading = false;
      state.thoughtsStreaming = false;
      state.isWaitingForFallback = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchChatHistory pending
      .addCase(fetchChatHistory.pending, (state) => {
        state.isChatHistoryLoading = true;
        state.notification = null;
      })
      // Handle fetchChatHistory fulfilled
      .addCase(fetchChatHistory.fulfilled, (state) => {
        state.isChatHistoryLoading = false;
      })
      // Handle fetchChatHistory rejected
      .addCase(fetchChatHistory.rejected, (state, action) => {
        console.error(
          '❌ [Redux] fetchChatHistory.rejected:',
          action.error.message
        );
        state.isChatHistoryLoading = false;
        state.notification = {
          message: action.error.message || 'Failed to load chat history',
          type: 'error',
          sessionId: state.sessionId || '', // Use current session from state
        };
      })
      // Issue #886: Handle translateThoughtsForMessage pending
      // Track message ID as being translated (for shimmer loading state)
      // Only show shimmer when showShimmer=true (default). Page reload translations are silent.
      .addCase(translateThoughtsForMessage.pending, (state, action) => {
        const { messageId, showShimmer = true } = action.meta.arg;
        // Only add to translating list if shimmer should be shown
        // Page reload translations use showShimmer=false for silent background translation
        if (showShimmer && !state.translatingMessageIds.includes(messageId)) {
          state.translatingMessageIds.push(messageId);
        }
      })
      // Issue #886: Handle translateThoughtsForMessage fulfilled
      // Updates the message's thinking field with the Slovak translation
      // Also updates executedStages.thoughts for stages that have embedded thoughts
      .addCase(translateThoughtsForMessage.fulfilled, (state, action) => {
        const { messageId, translatedThoughts } = action.payload;
        // Remove from translating list
        state.translatingMessageIds = state.translatingMessageIds.filter(
          (id) => id !== messageId
        );
        const message = state.chatHistory.entities[messageId];
        if (message) {
          // Update thinking field
          const changes: Partial<ChatHistoryItem> = {
            thinking: translatedThoughts,
          };

          // Issue #886: Also update executedStages if they have embedded thoughts
          // This ensures stage.thoughts displays translated content, not English
          if (message.executedStages && message.executedStages.length > 0) {
            const updatedStages = message.executedStages.map((stage) => {
              // Only update stages that have thoughts (refiningLegislationStatus, webSearchFallback)
              if (
                (stage.stageKey === 'refiningLegislationStatus' ||
                  stage.stageKey === 'webSearchFallback') &&
                stage.thoughts
              ) {
                return { ...stage, thoughts: translatedThoughts };
              }
              return stage;
            });
            changes.executedStages = updatedStages;
          }

          chatHistoryAdapter.updateOne(state.chatHistory, {
            id: messageId,
            changes,
          });
        }
      })
      // Issue #886: Handle translateThoughtsForMessage rejected
      // Remove from translating list on error (original English will be shown)
      .addCase(translateThoughtsForMessage.rejected, (state, action) => {
        const messageId = action.meta.arg.messageId;
        state.translatingMessageIds = state.translatingMessageIds.filter(
          (id) => id !== messageId
        );
        console.warn(
          `🌍 [Redux] Translation failed for ${messageId}:`,
          action.error.message
        );
      })
      // ✅ Paginated chat history - pending
      .addCase(fetchChatHistoryPaginated.pending, (state, action) => {
        const isLoadMore = action.meta.arg.cursor !== undefined;
        if (isLoadMore) {
          state.isLoadingMore = true;
        } else {
          state.isChatHistoryLoading = true;
        }
        state.notification = null;
      })
      // ✅ Paginated chat history - fulfilled
      .addCase(fetchChatHistoryPaginated.fulfilled, (state, action) => {
        const { chatHistoryItems, hasMore, nextCursor, isLoadMore } =
          action.payload;

        // Update pagination state
        state.hasMoreMessages = hasMore;
        state.nextCursor = nextCursor;

        if (isLoadMore) {
          // Load more: prepend older messages to the beginning
          // Use upsertMany to add without duplicates
          chatHistoryAdapter.upsertMany(state.chatHistory, chatHistoryItems);
          state.isLoadingMore = false;
        } else {
          // Initial load: replace all messages
          chatHistoryAdapter.setAll(state.chatHistory, chatHistoryItems);
          state.isChatHistoryLoading = false;
        }
      })
      // ✅ Paginated chat history - rejected
      .addCase(fetchChatHistoryPaginated.rejected, (state, action) => {
        const isLoadMore = action.meta.arg.cursor !== undefined;
        if (isLoadMore) {
          state.isLoadingMore = false;
        } else {
          state.isChatHistoryLoading = false;
        }
        console.error(
          '❌ [Redux] fetchChatHistoryPaginated.rejected:',
          action.error.message
        );
        state.notification = {
          message: action.error.message || 'Failed to load chat history',
          type: 'error',
          sessionId: state.sessionId || '',
        };
      });
  },
});

export const {
  initializeSession,
  addMessage,
  addMessageBatch, // ✅ NEW: Phase 4 progressive loading
  updateMessage, // New action
  // New action
  updateOptimisticUserMessageDbId, // Fix chat ordering bug
  setChatHistoryLoading, // ✅ NEW: Phase 4 progressive loading
  setInitializing, // ✅ NEW: Phase 4 initial loading
  setStreamStatus,
  appendEnhanceQuery,

  addAnalyseLegislation,

  setRefinementOutput,
  appendFilterOutput,

  clearStreamOutputs,
  setCurrentQuery,
  setCurrentChatId,
  streamStart,
  streamEnd,
  streamCancelled,
  clearNotification, // Renamed from clearError
  setNotification, // #11755: stopStream's read-only-403 message
  // Export new action
  appendRefinementThoughts,
  setRefinementThoughts,
  setTranslatedThoughts, // Issue #886: Set Slovak translated thoughts
  setThoughtsStreaming,
  setCurrentResponseSourceType, // Issue #852: Track response source during streaming
  setIsWaitingForFallback, // Issue #852: Track waiting for fallback
  setUriMap, // Progressive link rendering: URI short-code mapping
  setWebSourcesMap, // #3817: rich web grounding source info for hover-card chips
  setTemporalVerifierForSession, // #5069: store temporal verifier SSE result per session
  clearTemporalVerifierForSession, // #5069: clear stale result at stream start
  setSemanticVerifierForSession, // #5069: store semantic verifier SSE result per session
  clearSemanticVerifierForSession, // #5069: clear stale result at stream start
  setAuthorityVerifierForSession, // #5773: store authority verifier SSE result per session
  clearAuthorityVerifierForSession, // #5773: clear stale result at stream start
  setContextChipSkippedForSession, // #8095: store skipped-chip SSE result per session
  clearContextChipSkippedForSession, // #8095: clear stale result at stream start
  setContextScopeChangedForSession, // store context-scope-change SSE result per session
  clearContextScopeChangedForSession, // clear stale result at stream start
  // Issue #852: Unified Streaming Model
  startStreamingMessage,
  appendToStreamingMessage,
  replaceStreamingMessageContent,
  completeStreamingMessage,
  // Issue #7155: doc-gen authoritative reconciliation (final document from DB)
  applyDocumentGenerationResult,
  appendStreamingDocument,
  clearStreamingDocument,
  // Progressive document analysis streaming
  addDiscoveredIssue,
  clearDiscoveredIssues,
  syncDiscoveredIssuesToStreamingMessage,
  addPendingComparison,
  clearPendingComparisons,
  syncPendingComparisonsToStreamingMessage,
  incrementComparisonCount,
  resetComparisonCount,
  addDiscoveredComparison,
  clearDiscoveredComparisons,
  setDocAnalysisStatus,
  // Timeline stages tracking
  addExecutedStage,
  updateExecutedStage,
  clearExecutedStagesQueue, // Issue #886: Clear stages for fallback messages
  // Session-scoped filter isolation
  freezeSessionFilters,
  applySessionSmartFilters,

  // Issue #886: Session-scoped smart filter loading
  setAutoFilterLoading,
  // Search documents: atomic search results message
  addSearchResultsMessage,
  // Race-condition fix: set sessionId early before async fetchChatHistory
  setSessionId,
} = researchSessionSlice.actions;

// Export adapter selectors for use in components
export const chatHistorySelectors = chatHistoryAdapter.getSelectors<{
  researchSession: ResearchSessionState;
}>((state) => state.researchSession.chatHistory);

export default researchSessionSlice.reducer;
